import type { CatalogosDelInforme, Informe, PestanaConCuadro, SeccionInforme } from "@/lib/informes";
import { informeTieneContenidos } from "@/lib/seleccionDeContenidos";

import { definicionDesdeInforme, obligatoriedadDe } from "./definicionDeDocumentos";
import { archivosSinClasificar } from "./documentosDeLaPestana";

/**
 * En qué estado está una pestaña del editor.
 *
 * SON SEIS Y NO DOS, y esa es la corrección de fondo del rediseño. Hasta acá el
 * punto de color era rojo o verde, así que "nadie la abrió todavía" y "falta un
 * dato obligatorio" se pintaban igual: un informe recién aceptado mostraba
 * CINCO pestañas en rojo sin que nadie hubiera tenido oportunidad de tocarlas,
 * y el rojo dejaba de significar algo.
 *
 * Ninguno de los seis es un dato nuevo: los seis se DERIVAN de lo que el
 * expediente ya guarda.
 */
export type ClaveDeEstadoDePestana =
  /** Falta un dato obligatorio. Es lo único que bloquea. */
  | "falta"
  /** Empezada y sin terminar. */
  | "curso"
  /** Sin pendientes. */
  | "listo"
  /** Todavía no la empezó nadie. */
  | "nuevo"
  /** Alguien declaró que no corresponde. */
  | "na"
  /** Solo lectura: no reclama nada. Son las dos de mercado. */
  | "dato";

export interface EstadoDeLaPestana {
  estado: ClaveDeEstadoDePestana;
  /**
   * Lo que se imprime a la derecha del rótulo: "1/3", "24", "n/a" o "—".
   *
   * `null` es "esta pestaña no tiene cómo contarse", que NO es lo mismo que
   * "—" (que es un cero medido). Hoy le pasa a "Antecedentes generales" y a las
   * de mercado mientras su conteo viaja.
   */
  chip: string | null;
}

/** Lo único que estas cuentas miran de una fila. */
interface FilaValidable {
  validada: boolean;
}

/**
 * El estado de una lista con su casilla de "no aplica" — el patrón que
 * comparten los cuatro cuadros técnicos y el descriptor de aguas.
 *
 * EL ORDEN DE LAS TRES PREGUNTAS IMPORTA. Primero "¿alguien declaró que no
 * corresponde?", después "¿hay algo cargado?" y recién entonces "¿está todo
 * validado?". Al revés, un predio de secano declarado se leería como una
 * pestaña vacía que nadie abrió.
 */
function desdeUnaLista(filas: FilaValidable[], noAplica: boolean | null): EstadoDeLaPestana {
  if (noAplica === true) return { estado: "na", chip: "n/a" };
  if (filas.length === 0) return { estado: "nuevo", chip: "—" };

  const validadas = filas.filter((f) => f.validada).length;

  return {
    estado: validadas === filas.length ? "listo" : "curso",
    chip: `${validadas}/${filas.length}`,
  };
}

/** Las filas del cuadro de una pestaña técnica, sea cual sea. */
function filasDelCuadro(informe: Informe, cuadro: PestanaConCuadro): FilaValidable[] {
  return (informe[cuadro] ?? []) as FilaValidable[];
}

/**
 * Lo validable de "Descripción de suelos": las dos prosas y cada fila del
 * cuadro.
 *
 * SOLO CUENTA LO ESCRITO. Un campo vacío no entra al total, porque pedir que se
 * valide un texto que nadie redactó dejaría la pestaña reclamando para siempre
 * en todo informe recién aceptado.
 *
 * Es el criterio que traía `faltaValidarSuelos`, que vivía en `lib/informes.ts`
 * y se retiró al escribir esto: devolvía un booleano y acá hace falta el
 * CONTEO, así que dejarla habría sido tener dos definiciones de qué cuenta como
 * validado en esta pestaña, sin nada que las obligue a coincidir.
 */
function validablesDeSuelos(informe: Informe): FilaValidable[] {
  const seccion = informe.secciones?.suelos;
  const validables: FilaValidable[] = [];

  if (seccion?.descripcion_general?.trim()) {
    validables.push({ validada: !!seccion.descripcion_general_validada });
  }
  if (informe.topografia?.trim()) {
    validables.push({ validada: !!informe.topografia_validada });
  }

  return [...validables, ...((informe.clases_de_suelo ?? []) as FilaValidable[])];
}

/**
 * Lo validable del descriptor de aguas: la descripción general (si hay texto) y
 * las filas de las DOS listas.
 *
 * Misma regla que suelos: SOLO CUENTA LO ESCRITO. Una descripción vacía no
 * entra al total, o la pestaña reclamaría para siempre en todo informe recién
 * aceptado.
 */
function validablesDeAguas(informe: Informe): FilaValidable[] {
  const seccion = informe.secciones?.agua;
  const validables: FilaValidable[] = [];

  if (seccion?.descripcion_general?.trim()) {
    validables.push({ validada: !!seccion.descripcion_general_validada });
  }

  return [
    ...validables,
    ...((informe.agua_fuentes ?? []) as FilaValidable[]),
    ...((informe.agua_obras ?? []) as FilaValidable[]),
  ];
}

/**
 * El estado de "Antecedentes legales": cuántos documentos OBLIGATORIOS ya
 * tienen al menos un archivo.
 *
 * Es la única pestaña de documentos que reclama (Rodgers, 03-10-2026). Un
 * documento cuenta como obligatorio si su obligatoriedad RESUELTA lo es —la
 * ausencia de fila es Obligatorio—; Opcional y No aplica no se exigen y salen
 * del conteo. Un archivo cubre solo al documento de su propio tipo.
 *
 * Sin ningún obligatorio no hay nada que reclamar ni que contar: queda
 * "listo" y sin contador, porque "0/0" sería una cifra sin sentido.
 */
function desdeLosLegales(informe: Informe, catalogos: CatalogosDelInforme): EstadoDeLaPestana {
  const definicion = definicionDesdeInforme(informe.documentos);
  const conArchivo = new Set((informe.archivos ?? []).map((a) => a.documento_tipo_id));
  const obligatorios = (catalogos.documentos?.legal ?? []).filter(
    (d) => obligatoriedadDe(definicion, d.id) === "obligatorio",
  );

  /* LO QUE EL CLIENTE MANDÓ Y NADIE ORDENÓ también es trabajo pendiente: la IA
     no lo lee hasta que se clasifica. Nunca pinta rojo —no es un dato que
     falte—, pero tampoco deja la pestaña en verde. */
  const sinClasificar = archivosSinClasificar(informe.archivos ?? [], "legal").length;

  if (obligatorios.length === 0) {
    return sinClasificar > 0
      ? { estado: "curso", chip: String(sinClasificar) }
      : { estado: "listo", chip: null };
  }

  const cubiertos = obligatorios.filter((d) => conArchivo.has(d.id)).length;

  return {
    estado:
      cubiertos < obligatorios.length ? "falta" : sinClasificar > 0 ? "curso" : "listo",
    chip: `${cubiertos}/${obligatorios.length}`,
  };
}

/**
 * El estado de una pestaña del EXPEDIENTE.
 *
 * Es puro y vive fuera del componente a propósito: es la única lógica de esta
 * barra que puede estar mal sin que nada se vea roto —una pestaña verde que
 * debía estar en rojo no se distingue mirando— y dentro del árbol un test no
 * podría separarla del render.
 */
export function estadoDeLaPestana(
  seccion: SeccionInforme,
  informe: Informe,
  /* Solo lo lee la rama de legales, que cuenta contra el catálogo. */
  catalogos: CatalogosDelInforme,
): EstadoDeLaPestana {
  const noAplica = (nombre: SeccionInforme) => informe.secciones?.[nombre]?.no_aplica ?? null;

  switch (seccion) {
    /* SIN CONTADOR, y es deliberado. El diseño dibuja "6/9" sobre un desglose
       en nueve secciones del CONTENIDO de esta pestaña, que hoy no existe y que
       se trabaja aparte. Un contador inventado sería peor que ninguno: acá se
       mide avance, y una cifra que nadie calculó se lee igual que una medida.
       El punto de color sí sale del mismo criterio de siempre. */
    case "general":
      return {
        estado:
          !informeTieneContenidos(informe) || !informe.cliente_nombre ? "falta" : "listo",
        chip: null,
      };

    /* LEGALES VUELVE A RECLAMAR (Rodgers, 03-10-2026), y esto revierte SOLO
       para esta pestaña la regla del 29-09-2026. Aquella decía que ninguna de
       las dos reclamaba porque todo documento nace Obligatorio y no queda nada
       por DEFINIR. Lo que se reclama ahora es otra cosa: que LLEGUEN los
       archivos de los obligatorios. Rojo con "0/2" mientras falte alguno, verde
       cuando están todos; ver `desdeLosLegales`. */
    case "legal":
      return desdeLosLegales(informe, catalogos);

    /* HISTÓRICOS SIGUE SIN RECLAMAR EN ROJO: son antecedentes internos que el equipo
       consigue por su cuenta, no se le piden al cliente, así que la falta de un
       archivo no es un dato que bloquee. Lo que falta se ve adentro, en el
       resumen de la pestaña. */
    case "historico": {
      /* Lo único que la mantiene pendiente: tasaciones y documentación
         descriptiva que el cliente mandó y nadie clasificó. */
      const sinClasificar = archivosSinClasificar(informe.archivos ?? [], "historico").length;

      return sinClasificar > 0
        ? { estado: "curso", chip: String(sinClasificar) }
        : { estado: "listo", chip: null };
    }

    /* Lo que se cuenta es la casilla "Información validada" de cada rol: es el
       único hecho que distingue una descripción que leyó una persona de una que
       propuso la IA y nadie miró. */
    case "descripcion":
      return desdeUnaLista(
        (informe.roles ?? []).map((rol) => ({ validada: !!rol.descripcion_deslindes_validada })),
        null,
      );

    /* `no_aplica` va en `null` a propósito para suelos: un predio SIEMPRE tiene
       suelo, así que esa casilla no significa nada acá y la pestaña nunca cae
       en gris. */
    case "suelos":
      return desdeUnaLista(validablesDeSuelos(informe), null);

    /* LAS DOS LISTAS JUNTAS. Un predio con un tranque cargado y ninguna fuente
       ya está empezado; separarlas daría dos contadores para una sola pestaña. */
    case "agua":
      return desdeUnaLista(validablesDeAguas(informe), noAplica("agua"));

    // El cuadro de maquinaria vive en `maquinarias`, en plural: la sección y la
    // colección no se llaman igual solo en esta.
    case "maquinaria":
      return desdeUnaLista(filasDelCuadro(informe, "maquinarias"), noAplica("maquinaria"));

    case "instalaciones":
    case "construcciones":
    case "plantaciones":
      return desdeUnaLista(filasDelCuadro(informe, seccion), noAplica(seccion));
  }

  /* El guardián de exhaustividad, igual que en `guardarYSalir`: si entra una
     sección nueva sin su rama, esto no compila. Sin él, la pestaña saldría con
     un estado inventado y nadie se enteraría hasta verla del color equivocado. */
  const _seccionSinEstado: never = seccion;
  return { estado: "nuevo", chip: null };
}

/**
 * El estado de una pestaña de MERCADO (CBR y Ofertas).
 *
 * SON SOLO LECTURA Y NUNCA RECLAMAN NADA, ni con cero fichas: un informe sin
 * comparables no está incompleto, está en una etapa anterior. Por eso no
 * comparten vocabulario con las del expediente y tienen su propio estado.
 *
 * `cantidad` en `null` es "el conteo todavía no llegó", y ahí no se dibuja
 * nada: un `0` que medio segundo después se convierte en 24 afirma algo falso
 * sobre el informe.
 */
export function estadoDeMercado(cantidad: number | null): EstadoDeLaPestana {
  return { estado: "dato", chip: cantidad === null ? null : String(cantidad) };
}

/**
 * El estado de la pestaña de REGISTRO DE TERRENO.
 *
 * Es solo lectura como las de mercado y por lo mismo nunca reclama nada: un
 * informe sin registros no está incompleto, el encargado todavía no
 * sincronizó. El chip es el total de fotos, audios y textos que ya trae la
 * misma consulta que dibuja la pestaña; `null` mientras esa consulta no
 * responde, para no afirmar un cero que medio segundo después es otro número.
 *
 * Delega en `estadoDeMercado` y no la copia: lo que comparten es la regla
 * "solo lectura, con conteo", no la fuente del conteo.
 */
export function estadoDeTerreno(cantidad: number | null): EstadoDeLaPestana {
  return estadoDeMercado(cantidad);
}
